import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtCcy } from "@/lib/format";

const PCTS = [5, 10, 15, 20];

const Chip = ({ label, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex-1 rounded-full border py-1 text-xs font-medium transition-colors ${
      active ? "border-[color:var(--tharwy-lime)] bg-secondary font-semibold" : "hover:bg-secondary"}`}>
    {label}
  </button>
);

/** The Stop loss panel: a fixed price you won't hold below, or a stop that trails the high. */
export const StopPanel = ({ symbol, quote, qty, stopPrice, setStopPrice, trailPct, setTrailPct }) => {
  const trailing = trailPct != null;
  return (
    <div className="space-y-2.5 rounded-xl border p-3">
      <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-secondary p-1">
        {[["fixed", "Fixed stop"], ["trailing", "Trailing stop"]].map(([key, label]) => (
          <button key={key} type="button"
            onClick={() => setTrailPct(key === "trailing" ? (trailPct ?? 10) : null)}
            data-testid={`order-ticket-stop-mode-${key}`}
            className={`rounded-md py-1.5 text-xs font-semibold transition-colors ${
              (key === "trailing") === trailing
                ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {trailing ? (
        <>
          <Label>Trail this far below the high</Label>
          <div className="flex gap-1.5">
            {PCTS.map((pct) => (
              <Chip key={pct} label={`${pct}%`} active={trailPct === pct}
                onClick={() => setTrailPct(pct)} testid={`order-ticket-trail-${pct}`} />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="order-ticket-trail-hint">
            The stop starts at {fmtCcy(quote.price * (1 - trailPct / 100), quote.currency)} and climbs
            every time {symbol} makes a new high — it never moves back down. If the price falls
            {" "}{trailPct}% off its high we sell {qty} at the market, so gains lock in without you watching.
          </p>
        </>
      ) : (
        <>
          <Label>Sell if it falls to ({quote.currency})</Label>
          <Input type="number" value={stopPrice} onChange={(e) => setStopPrice(e.target.value)}
            placeholder={(quote.price * 0.9).toFixed(2)} data-testid="order-ticket-stop-input" />
          <div className="flex gap-1.5">
            {PCTS.map((pct) => (
              <Chip key={pct} label={`-${pct}%`} testid={`order-ticket-stop-${pct}`}
                onClick={() => setStopPrice((quote.price * (1 - pct / 100)).toFixed(2))} />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="order-ticket-stop-hint">
            {stopPrice
              ? `We sell ${qty} ${symbol} at the market if it trades down to ${fmtCcy(Number(stopPrice), quote.currency)} — ${(((quote.price - Number(stopPrice)) / quote.price) * 100).toFixed(1)}% below now. Your shares stay reserved until then.`
              : "Pick the price you are no longer willing to hold below. A fast market can fill lower than your stop."}
          </p>
        </>
      )}
    </div>
  );
};
